import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
  Route,
  BrowserRouter as Router,
  Navigate,
  Routes,
} from "react-router-dom";
import LoginScreen from "./pages/login";
import Homepage from "./pages/home";
import { setUserLoginStatus } from "./store/userStatus";
import MoiEntry from "./pages/entry";
import MoiList from "./pages/list";
import TrainingPage from "./pages/training";
import { FaShieldAlt, FaCheckCircle } from "react-icons/fa";

const RedirectIfAuthenticated = ({ children }) => {
  const dispatch = useDispatch();
  const findToken = localStorage.getItem("dwt-entry-tok");

  if (findToken && findToken.length > 0) {
    dispatch(setUserLoginStatus(true));
    return <Navigate to="/home" replace />;
  }

  dispatch(setUserLoginStatus(false));
  return children;
};

const PolicyAcceptModal = ({ onAccept }) => {
  const [accepted, setAccepted] = useState(false);

  const handleAcceptClick = () => {
    setAccepted(true);
    setTimeout(() => {
      onAccept();
    }, 450); // Allow checkmark animation to complete
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-md p-4">
      <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 shadow-2xl border border-slate-100 flex flex-col items-center text-center animate-[modalSlideUp_0.4s_cubic-bezier(0.16,1,0.3,1)]">
        <style>{`
          @keyframes modalSlideUp {
            from { opacity: 0; transform: translateY(24px) scale(0.96); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
          @keyframes scaleUp {
            from { transform: scale(0.8); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
          }
        `}</style>
        
        <div className="w-16 h-16 bg-amber-50 border border-amber-100 text-amber-600 rounded-full flex items-center justify-center mb-6 shrink-0">
          <FaShieldAlt className="text-3xl animate-pulse" />
        </div>

        <h2 className="text-2xl font-black text-slate-800 tracking-tight mb-3">
          Responsibility Agreement
        </h2>

        <p className="text-slate-500 text-xs leading-relaxed mb-6 font-medium">
          Please review and accept our safety and operational policy before continuing to the operator desk.
        </p>

        <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100 text-left mb-6 text-xs text-slate-600 leading-relaxed space-y-3 font-semibold">
          <p>
            1. I hereby agree that all payments and physical things I receive from people during this event will be fully handled by me.
          </p>
          <p>
            2. I will take complete care of the safety and custody of all physical properties and money logged under my operator session.
          </p>
          <p>
            3. I am absolutely sure that all safety precautions and records will be properly taken care of by me.
          </p>
        </div>

        <button
          onClick={handleAcceptClick}
          disabled={accepted}
          className={`w-full py-3.5 text-white font-bold text-sm rounded-xl transition flex items-center justify-center gap-2 group hover:opacity-90 active:scale-[0.99] ${
            accepted
              ? "bg-emerald-600 shadow-md shadow-emerald-600/20"
              : "bg-[linear-gradient(135deg,#ff8c00,#f97316)] hover:from-orange-600 hover:to-orange-700 shadow-md shadow-orange-500/20"
          }`}
        >
          {accepted ? (
            <>
              <FaCheckCircle className="animate-[scaleUp_0.3s_ease-out]" />
              <span>Accepted Successfully</span>
            </>
          ) : (
            <span>I Agree & Accept Policy</span>
          )}
        </button>
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const dispatch = useDispatch();
  const findToken = localStorage.getItem("dwt-entry-tok");
  const [policyAccepted, setPolicyAccepted] = useState(
    sessionStorage.getItem("policy-accepted") === "true"
  );

  if (!findToken || findToken.length === 0) {
    dispatch(setUserLoginStatus(false));
    return <Navigate to="/login" replace />;
  }

  dispatch(setUserLoginStatus(true));

  if (!policyAccepted) {
    return (
      <PolicyAcceptModal
        onAccept={() => {
          sessionStorage.setItem("policy-accepted", "true");
          setPolicyAccepted(true);
        }}
      />
    );
  }

  return children;
};

const AppNavigation = () => {
  const hasToken = !!localStorage.getItem("dwt-entry-tok");

  return (
    <Router>
      <Routes>
        <Route
          path="/login"
          element={
            <RedirectIfAuthenticated>
              <LoginScreen />
            </RedirectIfAuthenticated>
          }
        />
        <Route
          path="/home"
          element={
            <ProtectedRoute>
              <Homepage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/moibook"
          element={
            <ProtectedRoute>
              <MoiEntry />
            </ProtectedRoute>
          }
        />
        <Route
          path="/moilist"
          element={
            <ProtectedRoute>
              <MoiList />
            </ProtectedRoute>
          }
        />
        <Route path="/training" element={<TrainingPage />} />
        <Route
          path="*"
          element={<Navigate to={hasToken ? "/home" : "/login"} replace />}
        />
      </Routes>
    </Router>
  );
};

export default AppNavigation;
